const ICE_GATHERING_TIMEOUT = 5000;
const DEFAULT_ICE_SERVERS = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
];
const DEFAULT_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};
const DEFAULT_REMOTE_AUDIO_VOLUME = 0.4;
const REACHABLE_ICE_CANDIDATE_REGEX =
  /a=candidate:[^\r\n]+ typ (srflx|relay)(?: |\r|\n|$)/;

const configuredIceServers = () => {
  const servers = window.chatwootConfig?.whatsappWabaCallingIceServers;
  return Array.isArray(servers) && servers.length
    ? servers
    : DEFAULT_ICE_SERVERS;
};

const audioConstraints = () => {
  const configured = window.chatwootConfig?.whatsappWabaCallingAudioConstraints;
  if (
    configured &&
    typeof configured === 'object' &&
    !Array.isArray(configured)
  ) {
    return { ...DEFAULT_AUDIO_CONSTRAINTS, ...configured };
  }

  return DEFAULT_AUDIO_CONSTRAINTS;
};

const remoteAudioVolume = () => {
  const volume = Number(
    window.chatwootConfig?.whatsappWabaCallingRemoteAudioVolume
  );
  if (!Number.isFinite(volume)) return DEFAULT_REMOTE_AUDIO_VOLUME;

  return Math.min(Math.max(volume, 0), 1);
};

const normalizedSignaling = (signaling, defaultType) => {
  if (typeof signaling === 'string') {
    return { sdp: signaling, sdp_type: defaultType };
  }
  if (!signaling?.sdp) return null;

  return { sdp: signaling.sdp, sdp_type: signaling.sdp_type || defaultType };
};

class WabaWebRtcClient {
  constructor() {
    this.peer = null;
    this.localStream = null;
    this.remoteStream = null;
    this.remoteAudio = null;
  }

  isSupported() {
    return Boolean(
      this.peer ||
        (window.RTCPeerConnection && navigator.mediaDevices?.getUserMedia)
    );
  }

  async outboundOffer() {
    await this.build();
    try {
      await this.peer.setLocalDescription(await this.peer.createOffer());
      await this.waitForIce();
      const session = this.localDescription();
      if (!REACHABLE_ICE_CANDIDATE_REGEX.test(session.sdp)) {
        throw new Error('missing_reachable_ice_candidate');
      }

      return session;
    } catch (error) {
      this.close();
      throw error;
    }
  }

  async inboundAnswer(signaling) {
    const offer = normalizedSignaling(signaling, 'offer');
    if (!offer?.sdp) throw new Error('missing_remote_sdp');

    await this.build();
    try {
      await this.peer.setRemoteDescription({
        type: offer.sdp_type,
        sdp: offer.sdp,
      });
      await this.peer.setLocalDescription(await this.peer.createAnswer());
      await this.waitForIce();
      return this.localDescription();
    } catch (error) {
      this.close();
      throw error;
    }
  }

  async applyAnswer(signaling) {
    const answer = normalizedSignaling(signaling, 'answer');
    if (!this.peer || !answer?.sdp || this.peer.remoteDescription) return;

    await this.peer.setRemoteDescription({
      type: answer.sdp_type,
      sdp: answer.sdp,
    });
  }

  close() {
    this.localStream?.getTracks().forEach(track => track.stop());
    this.localStream = null;
    this.remoteStream = null;

    if (this.remoteAudio) {
      this.remoteAudio.pause();
      this.remoteAudio.srcObject = null;
      this.remoteAudio.remove();
      this.remoteAudio = null;
    }

    this.peer?.close();
    this.peer = null;
  }

  async build() {
    if (!this.isSupported()) throw new Error('webrtc_not_supported');

    this.close();
    this.peer = new window.RTCPeerConnection({
      iceServers: configuredIceServers(),
    });
    this.peer.ontrack = event => this.handleRemoteTrack(event);
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints(),
      video: false,
    });
    this.localStream.getTracks().forEach(track => {
      this.peer.addTrack(track, this.localStream);
    });
  }

  handleRemoteTrack(event) {
    const [stream] = event.streams || [];
    if (stream) {
      this.attachRemoteStream(stream);
      return;
    }

    // WhatsApp offers can omit a=msid. Build a MediaStream from the received
    // track so playback and browser echo cancellation still work.
    if (!event.track) return;
    if (!this.remoteStream) this.remoteStream = new MediaStream();
    this.remoteStream.addTrack(event.track);
    this.attachRemoteStream(this.remoteStream);
  }

  attachRemoteStream(stream) {
    if (!stream || typeof document === 'undefined') return;
    if (!this.remoteAudio) {
      this.remoteAudio = document.createElement('audio');
      this.remoteAudio.autoplay = true;
      this.remoteAudio.playsInline = true;
      this.remoteAudio.className = 'hidden';
      document.body.appendChild(this.remoteAudio);
    }

    this.remoteAudio.volume = remoteAudioVolume();
    this.remoteAudio.srcObject = stream;
    this.remoteAudio.play().catch(() => {});
  }

  localDescription() {
    return {
      sdp_type: this.peer.localDescription.type,
      sdp: this.peer.localDescription.sdp,
    };
  }

  waitForIce() {
    if (this.peer.iceGatheringState === 'complete') return Promise.resolve();

    return new Promise(resolve => {
      const peer = this.peer;
      let timeout;
      let cleanup = () => {};
      const onChange = () => {
        if (peer.iceGatheringState === 'complete') cleanup();
      };
      cleanup = () => {
        clearTimeout(timeout);
        peer.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      };

      timeout = setTimeout(cleanup, ICE_GATHERING_TIMEOUT);
      peer.addEventListener('icegatheringstatechange', onChange);
    });
  }
}

export default new WabaWebRtcClient();
